import type { Action, Competition, Group, GroupsConfig, Id, Phase, Player, Team, TeamInput, Tournament } from '../../types.js';
import { nextId } from '../../ids.js';
import { forfeitResult, walkoverResult } from '../../rules/validate.js';
import { EngineError, shortenName } from '../state.js';
import { clubKey, pairingKey, teamKey } from '../keys.js';
import { competition, involves, rulesOf, teamOf, uebernehmen } from './shared.js';
import { gruppeFuer, partnerGroupMatches, renumber } from './phases.js';
import { autoWalkovers, finishMatch } from './matches.js';
import { unpropagate } from '../../bracket/single.js';

// ---------------------------------------------------------------------------
// Teams
// ---------------------------------------------------------------------------

const TEAM_FELDER = ['name', 'shortName', 'club', 'seed', 'paid', 'checkedIn', 'status', 'note', 'answers'] as const;

export function applyTeam(t: Tournament, action: Action): string[] {
  const warnings: string[] = [];
  switch (action.type) {
    case 'team.add':
      warnings.push(...addTeam(t, competition(t, action.competitionId), action.team, action.groupId));
      break;
    case 'team.update': {
      const comp = competition(t, action.competitionId);
      const team = teamOf(comp, action.teamId);
      const warEingecheckt = team.checkedIn;
      // Auch von der Warteliste: ein Nachrücker nach der Auslosung bekommt seine Spiele (wie später Check-in)
      const warWeg = team.status === 'withdrawn' || team.status === 'disqualified' || team.status === 'waitlist';
      const warAktiv = team.status === 'registered' || team.status === 'confirmed';
      uebernehmen(team, action.patch, TEAM_FELDER);
      // Abgemeldet (ohne team.withdraw, z. B. vor der Auslosung): der Erste der Warteliste rückt nach
      if (warAktiv && (team.status === 'withdrawn' || team.status === 'disqualified')) warnings.push(...nachruecken(t, comp, action.at));
      // Zurückgezogen → wieder gemeldet (Daniel, 10/2026): Walkovers wieder öffnen, Annullierung aufheben
      if (warWeg && (team.status === 'registered' || team.status === 'confirmed')) {
        warnings.push(...wiederDabei(t, comp, team, action.at));
        break;
      }
      if (action.patch.name && !action.patch.shortName) team.shortName = shortenName(action.patch.name);
      // Verspäteter Check-in in eine Auslosung „nur eingecheckte Teams": das Team bekommt jetzt seine Spiele
      if (!warEingecheckt && team.checkedIn && (team.status === 'registered' || team.status === 'confirmed')) {
        const active = comp.phases.find((p) => p.type === 'groups' && (p.status === 'drawn' || p.status === 'running') && (p.config as GroupsConfig).checkedInOnly);
        if (active && !active.groups.some((g) => g.teamIds.includes(team.id))) warnings.push(...nachmelden(t, comp, team, undefined));
      }
      break;
    }
    case 'team.withdraw': {
      const comp = competition(t, action.competitionId);
      const warAktiv = ['registered', 'confirmed'].includes(teamOf(comp, action.teamId).status);
      warnings.push(...withdrawTeam(t, comp, action.teamId, action.mode, action.at));
      if (warAktiv) warnings.push(...nachruecken(t, comp, action.at));
      break;
    }
    case 'team.remove': {
      const comp = competition(t, action.competitionId);
      const used = comp.phases.some((p) => p.matches.some((m) => involves(m, action.teamId)));
      if (used) throw new EngineError('Team ist schon in Spielen — stattdessen zurückziehen');
      const warAktiv = ['registered', 'confirmed'].includes(teamOf(comp, action.teamId).status);
      comp.teams = comp.teams.filter((x) => x.id !== action.teamId);
      for (const p of comp.phases) for (const g of p.groups) g.teamIds = g.teamIds.filter((id) => id !== action.teamId);
      if (warAktiv) warnings.push(...nachruecken(t, comp, action.at));
      break;
    }
    case 'team.setPlayers': {
      const comp = competition(t, action.competitionId);
      const team = teamOf(comp, action.teamId);
      const neu = (action.players ?? [])
        .filter((p) => p && typeof p === 'object')
        .map((p) => ({ firstName: String(p.firstName ?? '').trim(), lastName: String(p.lastName ?? '').trim(), substitute: p.substitute === true }))
        .filter((p) => p.firstName || p.lastName);
      if (neu.length === 0) throw new EngineError('Mindestens ein Spieler mit Namen');
      const imSpiel = comp.phases.some((p) => p.matches.some((m) => involves(m, team.id)));
      if (imSpiel && neu.length !== team.players.length) throw new EngineError('Team ist schon in Spielen — nur Namen ändern, nicht die Anzahl der Spieler');
      // Stand der Teamname auf dem alten Standardnamen, zieht er mit
      const standard = (pl: { firstName: string; lastName: string }[]) =>
        new Set(
          [
            pl.length === 1 ? `${pl[0]?.firstName ?? ''} ${pl[0]?.lastName ?? ''}`.trim() : '',
            pl.length === 1 ? (pl[0]?.firstName ?? '') : '',
            pl.length === 1 ? (pl[0]?.lastName ?? '') : '',
            pl.map((x) => x.lastName || x.firstName).join('/'),
          ]
            .filter(Boolean)
            .map((x) => x.toLowerCase()),
        );
      const warStandard = standard(team.players).has(team.name.trim().toLowerCase());
      team.players = neu.map((p, i) => {
        const alt = team.players[i];
        const spieler: Player = { ...(alt ?? {}), id: alt?.id ?? nextId(t, 'p'), firstName: p.firstName, lastName: p.lastName };
        if (p.substitute) spieler.substitute = true;
        else delete spieler.substitute;
        return spieler;
      });
      if (warStandard) {
        const solo = neu.length === 1 ? `${neu[0]?.firstName ?? ''} ${neu[0]?.lastName ?? ''}`.trim() : '';
        team.name = solo || neu.map((p) => p.lastName || p.firstName).join('/') || team.name;
        team.shortName = shortenName(team.name);
      }
      if (neu.length !== comp.ruleSet.teamSize) warnings.push(`Team hat ${neu.length} Spieler, Bewerb erwartet ${comp.ruleSet.teamSize}`);
      break;
    }
    case 'team.clearAll': {
      const comp = competition(t, action.competitionId);
      // Sobald ausgelost ist, hängen Spiele an den Teams — dann ist Zurückziehen der Weg
      const gespielt = comp.phases.some((p) => p.matches.length > 0);
      if (gespielt) throw new EngineError('Es gibt schon Spiele — Teams einzeln zurückziehen');
      const n = comp.teams.length;
      if (n === 0) throw new EngineError('Keine Teams vorhanden');
      comp.teams = [];
      for (const p of comp.phases) for (const g of p.groups) g.teamIds = [];
      warnings.push(`${n} Teams gelöscht`);
      break;
    }
    case 'team.clubsFromNames': {
      const comp = competition(t, action.competitionId);
      // Nach der Auslosung nicht mehr: Die Spiele stünden dann im Widerspruch
      // zu den Mannschaften (es gäbe Paarungen innerhalb derselben Mannschaft)
      if (comp.phases.some((p) => p.matches.length > 0)) throw new EngineError('Es gibt schon Spiele — Mannschaften vorher setzen');
      let n = 0;
      for (const team of comp.teams) {
        if (team.club?.trim()) continue;
        // „Ceva 1" und „Mombarcaro1" → der Teil vor der Nummer
        const abgeleitet = team.name.replace(/\s*\d+\s*$/, '').trim();
        if (!abgeleitet || abgeleitet === team.name) continue;
        team.club = abgeleitet;
        n++;
      }
      if (n === 0) throw new EngineError('Aus den Teamnamen ließ sich keine Mannschaft ableiten');
      warnings.push(`${n} Paare einer Mannschaft zugeordnet`);
      break;
    }
  }
  return warnings;
}

function addTeam(t: Tournament, comp: Competition, input: TeamInput, groupId: Id | undefined): string[] {
  const warnings: string[] = [];
  const players = input.players.map((p) => ({
    id: nextId(t, 'p'),
    firstName: p.firstName.trim(),
    lastName: p.lastName.trim(),
    ...(p.club ? { club: p.club } : {}),
    ...(p.substitute ? { substitute: true } : {}),
  }));
  if (players.length !== comp.ruleSet.teamSize) warnings.push(`Team hat ${players.length} Spieler, Bewerb erwartet ${comp.ruleSet.teamSize}`);
  // Ein Spieler: voller Name („Marco Ferrero"), sonst Nachnamen mit „/" — sonst hießen drei Marcos alle „Marco" (Harald, 10/2026)
  const solo = players.length === 1 ? `${players[0]?.firstName ?? ''} ${players[0]?.lastName ?? ''}`.trim() : '';
  const name = input.name?.trim() || solo || players.map((p) => p.lastName || p.firstName).join('/') || 'Team';
  if (comp.teams.some((x) => x.name.toLowerCase() === name.toLowerCase())) warnings.push(`Teamname „${name}" existiert schon`);
  const team: Team = {
    id: nextId(t, 't'),
    name,
    shortName: shortenName(name),
    players,
    paid: input.paid ?? false,
    checkedIn: false,
    status: input.waitlist ? 'waitlist' : 'registered',
  };
  if (input.club) team.club = input.club;
  if (input.seed !== undefined) team.seed = input.seed;
  if (input.note) team.note = input.note;
  if (input.answers?.length) team.answers = input.answers.map((a) => ({ ...a }));
  comp.teams.push(team);
  if (team.status === 'waitlist') {
    warnings.push(`${team.name} steht auf der Warteliste`);
    return warnings;
  }

  // Nachmelden in eine laufende Gruppenphase (docs/11 §5.3) — bei „nur eingecheckte Teams" erst mit dem Check-in
  const active = comp.phases.find((p) => p.status === 'drawn' || p.status === 'running');
  if (active && (active.config as GroupsConfig).checkedInOnly && !groupId) {
    warnings.push('Team ist angelegt — seine Spiele kommen mit dem Check-in');
    return warnings;
  }
  if (active || groupId) warnings.push(...nachmelden(t, comp, team, groupId));
  return warnings;
}

/** Ein Team in eine schon ausgeloste Gruppenphase einreihen und seine Spiele erzeugen */
function nachmelden(t: Tournament, comp: Competition, team: Team, groupId: Id | undefined): string[] {
  const warnings: string[] = [];
  const active = comp.phases.find((p) => p.status === 'drawn' || p.status === 'running');
  {
    const phase = groupId ? comp.phases.find((p) => p.groups.some((g) => g.id === groupId)) : active;
    if (!phase) throw new EngineError('Gruppe nicht gefunden');
    if (phase.type !== 'groups') throw new EngineError('Nachmelden nur in eine Gruppenphase möglich — KO läuft schon');
    const group = groupId ? phase.groups.find((g) => g.id === groupId) : gruppeFuer(t, phase, team);
    if (!group) throw new EngineError('Keine Gruppe vorhanden');
    if (phase.frozen) warnings.push('Nachmeldung in eingefrorene Auslosung');
    if ((phase.config as GroupsConfig).partners) {
      // Wechselnde Partner: Der Plan einer Gruppe hängt an allen Spielern — nur eine Gruppe, in der noch nichts gespielt ist, wird neu erstellt
      const offen = (g: Group) => phase.matches.every((m) => m.groupId !== g.id || m.status === 'ready');
      const ziel = offen(group) ? group : groupId ? undefined : [...phase.groups].filter(offen).sort((x, y) => x.teamIds.length - y.teamIds.length)[0];
      if (!ziel) {
        if (groupId) throw new EngineError(`In Gruppe ${group.name} wird schon gespielt — mit wechselnden Partnern geht Nachmelden nur vorher`);
        // Späterer Check-in: der Check-in selbst gilt, nur Spiele gibt es keine mehr — nicht die ganze Aktion ablehnen
        warnings.push(`${team.name}: alle Gruppen spielen schon — mit wechselnden Partnern kein Nachmelden mehr möglich`);
        return warnings;
      }
      ziel.teamIds.push(team.id);
      phase.matches = phase.matches.filter((m) => m.groupId !== ziel.id);
      phase.matches.push(...partnerGroupMatches(t, phase, ziel));
      renumber(phase);
      warnings.push(`Nachgemeldet in Gruppe ${ziel.name}: Spielplan der Gruppe neu erstellt`);
      return warnings;
    }
    const maxRound = Math.max(0, ...phase.matches.filter((m) => m.groupId === group.id).map((m) => m.round));
    let round = maxRound;
    const skipOwnClub = (phase.config as GroupsConfig).noIntraClubMatches && team.club;
    const schon = new Set((phase.config as GroupsConfig).skipPairings ?? []);
    for (const other of group.teamIds) {
      if (skipOwnClub && clubKey(comp.teams.find((x) => x.id === other)?.club) === clubKey(team.club)) continue;
      if (schon.size && schon.has(pairingKey(teamKey(team), teamKey(comp.teams.find((x) => x.id === other) ?? { name: other })))) continue;
      round++;
      phase.matches.push({
        id: nextId(t, `m:${phase.id}`),
        phaseId: phase.id,
        groupId: group.id,
        round,
        number: 0,
        a: { kind: 'team', teamId: team.id },
        b: { kind: 'team', teamId: other },
        status: 'ready',
        priority: 0,
      });
    }
    group.teamIds.push(team.id);
    renumber(phase);
    warnings.push(`Nachgemeldet in Gruppe ${group.name}: ${group.teamIds.length - 1} zusätzliche Spiele`);
  }
  return warnings;
}

/**
 * Ein zurückgezogenes (oder disqualifiziertes) Team ist wieder dabei — „ist ja
 * ähnlich wie ein später Check-in". Spiele, die es per Walkover verloren hat,
 * werden wieder offen; annullierte Ergebnisse zählen wieder. Ein Spiel, von
 * dem im KO schon ein späteres abhängt, bleibt, wie es ist. Stand das Team in
 * keiner Gruppe (vor der Auslosung zurückgezogen), wird es nachgemeldet.
 */
/**
 * Warteliste (Daniel, 10/2026: „automatisch auf Warteliste setzen und
 * automatisch nachrücken lassen"): Wird ein angemeldetes Team abgemeldet oder
 * gelöscht, rückt der Erste der Warteliste (Reihenfolge der Anmeldung) nach —
 * nach der Auslosung wie ein später Check-in mit eigenen Spielen.
 */
function nachruecken(t: Tournament, comp: Competition, at: number): string[] {
  const naechster = comp.teams.find((x) => x.status === 'waitlist');
  if (!naechster) return [];
  naechster.status = 'registered';
  return [`${naechster.name} rückt von der Warteliste nach`, ...wiederDabei(t, comp, naechster, at)];
}

function wiederDabei(t: Tournament, comp: Competition, team: Team, at: number): string[] {
  const warnings: string[] = [];
  let offen = 0;
  let zaehlt = 0;
  let fest = 0;
  for (const phase of comp.phases) {
    if (phase.status === 'finished') continue;
    for (const m of phase.matches) {
      const side = involves(m, team.id);
      if (!side) continue;
      if (m.annulled) {
        delete m.annulled;
        zaehlt++;
      }
      // Wechselnde Partner: wegen der Abmeldung abgesagte Spiele wieder ansetzen
      if (m.status === 'cancelled' && m.cancelledFor === team.id) {
        delete m.cancelledFor;
        m.status = m.a.kind === 'team' && m.b.kind === 'team' ? 'ready' : 'open';
        offen++;
        continue;
      }
      if (m.status === 'walkover' && m.result?.endedBy === 'walkover' && m.result.winner !== side) {
        try {
          unpropagate(phase.matches, m);
        } catch {
          fest++;
          continue;
        }
        delete m.result;
        delete m.endedAt;
        m.status = m.a.kind === 'team' && m.b.kind === 'team' ? 'ready' : 'open';
        offen++;
      }
    }
    // Ist der Gegner seinerseits zurückgezogen, geht das Spiel gleich wieder per Walkover — diesmal an dieses Team
    autoWalkovers(t, comp, phase, at);
  }
  const aktiv = comp.phases.find((p) => p.type === 'groups' && (p.status === 'drawn' || p.status === 'running'));
  const cfg = aktiv?.config as GroupsConfig | undefined;
  if (aktiv && !aktiv.groups.some((g) => g.teamIds.includes(team.id)) && (!cfg?.checkedInOnly || team.checkedIn)) warnings.push(...nachmelden(t, comp, team, undefined));
  if (offen || zaehlt) warnings.push(`${team.name} ist wieder dabei: ${offen} Spiel${offen === 1 ? '' : 'e'} wieder offen, ${zaehlt} Ergebnis${zaehlt === 1 ? ' zählt' : 'se zählen'} wieder`);
  if (fest) warnings.push(`${fest} Walkover im KO bleibt — das nächste Spiel läuft schon`);
  return warnings;
}

/**
 * Abmelden bei wechselnden Partnern (Daniel, 10/2026: „wieso haben alle 2 Siege
 * und eine Niederlage?“ — Walkovers 0:21 trafen jeweils den Partner des
 * Abgemeldeten). Stattdessen:
 * - Hat in seiner Gruppe noch niemand gespielt: Spielplan ohne ihn neu.
 * - Sonst: seine offenen (und ein laufendes) Spiele entfallen; gespielte
 *   Ergebnisse bleiben für alle — sie zu annullieren träfe drei andere. Die
 *   Tabelle wertet dann pro Spiel (ungleiche Spielzahl).
 */
function partnerAbmelden(t: Tournament, phase: Phase, team: Team): string[] {
  const group = phase.groups.find((g) => g.teamIds.includes(team.id));
  if (!group) return [];
  const eigene = phase.matches.filter((m) => m.groupId === group.id);
  if (eigene.every((m) => m.status === 'ready' || m.status === 'cancelled')) {
    group.teamIds = group.teamIds.filter((id) => id !== team.id);
    phase.matches = phase.matches.filter((m) => m.groupId !== group.id);
    phase.matches.push(...partnerGroupMatches(t, phase, group));
    renumber(phase);
    return [`Gruppe ${group.name}: Spielplan ohne ${team.name} neu erstellt`];
  }
  let n = 0;
  for (const m of eigene) {
    if (!involves(m, team.id) || (m.status !== 'ready' && m.status !== 'open' && m.status !== 'running')) continue;
    if (m.status === 'running') {
      const court = t.courts.find((c) => c.currentMatchId === m.id);
      if (court) delete court.currentMatchId;
      delete m.startedAt;
      delete m.courtId;
      delete m.live;
    }
    m.status = 'cancelled';
    m.cancelledFor = team.id;
    n++;
  }
  return [`${team.name} abgemeldet: ${n} offene Spiele in Gruppe ${group.name} entfallen, gespielte bleiben — gewertet wird pro Spiel`];
}

function withdrawTeam(t: Tournament, comp: Competition, teamId: Id, mode: 'annul' | 'keep', at: number): string[] {
  const team = teamOf(comp, teamId);
  team.status = 'withdrawn';
  const warnings: string[] = [];
  for (const phase of comp.phases) {
    if (phase.status === 'finished') continue;
    if (phase.type === 'groups' && (phase.config as GroupsConfig).partners) {
      warnings.push(...partnerAbmelden(t, phase, team));
      continue;
    }
    const rules = rulesOf(comp, phase);
    // Nur Ergebnisse annullieren, die es *vor* dem Rückzug schon gab — nicht die
    // Walkovers, die diese Schleife selbst erzeugt
    const before = new Set(phase.matches.filter((m) => m.status === 'finished' || m.status === 'walkover').map((m) => m.id));
    for (const m of phase.matches) {
      const side = involves(m, teamId);
      if (!side) continue;
      if (before.has(m.id)) {
        if (mode === 'annul') m.annulled = true;
      } else if (m.status === 'running') {
        finishMatch(t, comp, phase, m, forfeitResult(rules, m.result?.sets ?? [], side), 'finished', at);
      } else if (m.status === 'ready') {
        finishMatch(t, comp, phase, m, walkoverResult(rules, side === 'A' ? 'B' : 'A'), 'walkover', at);
      }
      // open (Platzhalter-Gegner): wird beim Auffüllen per autoWalkovers erledigt
    }
    autoWalkovers(t, comp, phase, at);
  }
  warnings.push(mode === 'annul' ? 'Bisherige Ergebnisse annulliert, offene Spiele als Walkover gewertet' : 'Bisherige Ergebnisse bleiben, offene Spiele als Walkover gewertet');
  return warnings;
}
